import React from "react";
import { VEHICLES, DEALER, buildDealerUrl } from "@/data/dealership";
import { useT } from "@/i18n/lang";
import { ArrowUpRight, Fuel, Users } from "lucide-react";

export default function Inventory() {
  const { t } = useT();
  const [filter, setFilter] = React.useState("All");
  const categories = ["All", "Truck", "SUV", "Sedan", "Van"];
  const filtered = filter === "All" ? VEHICLES : VEHICLES.filter((v) => v.category === filter);

  return (
    <section id="inventory" data-testid="inventory-section" className="px-6 md:px-12 lg:px-16 py-20 md:py-32 bg-white">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-px bg-[#EB0A1E]" />
              <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#EB0A1E]">
                {t("inv.eyebrow")}
              </span>
            </div>
            <h2 className="font-display font-bold text-[#0A0A0A] text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              {t("inv.h1a")}<br />
              <span className="text-[#52525B]">{t("inv.h1b")}</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:flex md:items-end md:justify-end">
            <p className="text-[#52525B] max-w-sm">{t("inv.lede")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 border-b border-[#E4E4E7] pb-4">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 text-sm font-semibold transition-colors ${
                filter === c
                  ? "bg-[#0A0A0A] text-white"
                  : "text-[#52525B] hover:text-[#0A0A0A]"
              }`}
            >
              {t(`inv.cat.${c}`)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-12 auto-rows-[280px] md:auto-rows-[320px] gap-4 md:gap-6">
          {filtered.map((v, idx) => {
            const span =
              idx === 0
                ? "col-span-12 md:col-span-8 row-span-2"
                : idx === 1
                ? "col-span-12 md:col-span-4 row-span-1"
                : idx === 2
                ? "col-span-12 md:col-span-4 row-span-1"
                : idx % 3 === 0
                ? "col-span-12 md:col-span-6"
                : "col-span-12 md:col-span-6";
            return <VehicleCard key={v.id} v={v} span={span} large={idx === 0} />;
          })}
        </div>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-4 border-t border-[#E4E4E7] pt-8">
          <p className="text-[#52525B] text-sm md:text-base">{t("inv.cta")}</p>
          <a
            href={buildDealerUrl("/")}
            target="_blank"
            rel="noopener"
            data-testid="full-inventory-link"
            className="inline-flex items-center gap-2 text-[#0A0A0A] font-semibold border-b-2 border-[#EB0A1E] pb-1 hover:text-[#EB0A1E] transition-colors"
          >
            {t("inv.browseAll")} <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function VehicleCard({ v, span, large }) {
  return (
    <a
      href={buildDealerUrl("/")}
      target="_blank"
      rel="noopener"
      data-testid={`vehicle-card-${v.id}`}
      className={`vehicle-card relative ${span} group overflow-hidden border border-[#E4E4E7] block`}
      style={{
        background:
          "linear-gradient(160deg, #FFFFFF 0%, #F4F4F6 60%, #E9E9EC 100%)",
      }}
    >
      {/* Vehicle render — full PNG, contained */}
      <img
        src={v.image}
        alt={`Toyota ${v.name}`}
        className="absolute inset-x-0 top-0 w-full h-[68%] object-contain object-center px-4 pt-6 transition-transform duration-700 group-hover:scale-[1.04]"
        loading="lazy"
      />

      {/* Subtle road shadow */}
      <div className="absolute left-[12%] right-[12%] top-[58%] h-3 rounded-[50%] blur-md bg-black/15" />

      {/* Badge */}
      {v.badge && (
        <div className="absolute top-4 left-4 bg-[#EB0A1E] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 z-10">
          {v.badge}
        </div>
      )}

      {/* Hover arrow */}
      <div className="absolute top-4 right-4 w-9 h-9 bg-[#0A0A0A] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <ArrowUpRight className="w-4 h-4" />
      </div>

      {/* Info panel at bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#E4E4E7] px-5 py-4 md:px-6 md:py-5">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#EB0A1E] mb-0.5">
              Toyota · {v.bodyType}
            </div>
            <div
              className={`font-display font-bold text-[#0A0A0A] leading-none ${
                large ? "text-4xl md:text-5xl" : "text-2xl md:text-3xl"
              }`}
            >
              {v.name}
            </div>
            {large && (
              <div className="text-sm text-[#52525B] mt-2 hidden md:block">
                {v.tagline}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 text-[10px] uppercase tracking-wider text-[#52525B] flex-shrink-0">
            <span className="flex items-center gap-1">
              <Fuel className="w-3 h-3" /> {v.startingMpg}
            </span>
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" /> {v.seats} seats
            </span>
          </div>
        </div>
      </div>
    </a>
  );
}
